import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useNotify } from '@/hooks/useNotify';
import DetailPageLayout from '@/components/layout/DetailPageLayout';
import DetailPageHeader from '@/components/layout/DetailPageHeader';
import InvoiceDetailsCard from '@/components/invoices/details/InvoiceDetailsCard';
import InvoiceFinancialCard from '@/components/invoices/details/InvoiceFinancialCard';
import InvoiceAdditionalInfoCard from '@/components/invoices/details/InvoiceAdditionalInfoCard';
import InvoiceItemsTable from '@/components/invoices/details/InvoiceItemsTable';
import DocumentSendHistory from '@/components/shared/DocumentSendHistory';
import ApplyCreditsToInvoiceModal from '@/components/customers/credits/ApplyCreditsToInvoiceModal';
import { useCustomerCredits } from '@/hooks/useCustomerCredits';
import { PaymentRecordModal, WriteOffModal } from '@/components/invoices/payments';
import { usePaymentRecording } from '@/components/invoices/payments/usePaymentRecording';
import InvoicePreviewModal from '@/components/invoices/preview/InvoicePreviewModal';
import SendInvoiceDialog from '@/components/invoices/SendInvoiceDialog';
import { Edit, Trash2, Send, CreditCard, Download, Eye, CheckCircle2, AlertTriangle, RefreshCw, FileText, Copy } from 'lucide-react';

const InvoiceViewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const [showApplyCreditModal, setShowApplyCreditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showWriteOffModal, setShowWriteOffModal] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const { data: invoice, isLoading: isLoadingInvoice } = useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
        customers(
            id, name, last_name, customer_type, business_name, 
            representative_first_name, representative_last_name,
            street_address, suburb, city, province_state, postal_code, 
            cell_numbers, emails
          ),
          estimates(
            id, estimate_number, title, total_invoiced, invoicing_status, status
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Fetch linked project based on the estimate
  const { data: linkedProject } = useQuery({
    queryKey: ['invoiceProject', invoice?.estimate_id],
    queryFn: async () => {
      if (!invoice?.estimate_id) return null;
      
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, description, status, expected_start_date, expected_end_date, created_at')
        .eq('estimate_id', invoice.estimate_id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching linked project:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!invoice?.estimate_id,
  });

  const { data: lineItems, isLoading: isLoadingLineItems } = useQuery({
    queryKey: ['invoiceLineItems', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoice_line_items')
        .select('*')
        .eq('invoice_id', id)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!id,
  });

  // Fetch customer credits
  const { 
    credits: customerCredits = [],
    totalAvailableCredit = 0,
    applyCredit,
    isApplying
  } = useCustomerCredits(invoice?.customer_id || invoice?.customers?.id || null);

  const hasAvailableCredit = totalAvailableCredit > 0;
  const remainingBalance = (invoice?.total_amount || 0) - (invoice?.credit_applied || 0) - (invoice?.paid_amount || 0);
  const canApplyCredit = hasAvailableCredit && remainingBalance > 0 && invoice?.status !== 'paid' && invoice?.status !== 'cancelled';

  // Get the first available credit for the modal
  const availableCredit = customerCredits.find(c => c.status === 'issued' && c.remaining_amount > 0) || null;

  // Payment recording hook
  const { quickPay, isQuickPaying } = usePaymentRecording(id || '');

  // Delete mutation
  const deleteInvoiceMutation = useMutation({
    mutationFn: async () => {
      // Release reserved credits
      const { data: creditApps, error: fetchError } = await supabase
        .from('credit_applications')
        .select('id, credit_id, amount_applied')
        .eq('invoice_id', id!)
        .eq('application_status', 'reserved');

      if (fetchError) console.error('Error fetching credit applications:', fetchError);

      for (const app of creditApps || []) {
        const { data: credit } = await supabase
          .from('customer_credits')
          .select('remaining_amount, status')
          .eq('id', app.credit_id)
          .single();

        if (credit) {
          await supabase
            .from('customer_credits')
            .update({ 
              remaining_amount: (credit.remaining_amount || 0) + app.amount_applied,
              status: 'issued',
              updated_at: new Date().toISOString()
            })
            .eq('id', app.credit_id);
        }

        await supabase
          .from('credit_applications')
          .delete()
          .eq('id', app.id);
      }

      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', id!);

      if (error) throw error;
    },
    onSuccess: () => {
      notify.success('Invoice deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['customerCredits'] });
      navigate('/invoices');
    },
    onError: (error) => {
      console.error('Error deleting invoice:', error);
      notify.error(error);
    },
  });

  // Status update mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      if (invoice?.status === 'draft' && newStatus === 'sent') {
        await supabase
          .from('credit_applications')
          .update({ application_status: 'finalized' })
          .eq('invoice_id', id!)
          .eq('application_status', 'reserved');
      }
      
      const { error } = await supabase
        .from('invoices')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', id!);

      if (error) throw error;
    },
    onSuccess: () => {
      notify.success('Invoice status updated successfully');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['customerCredits'] });
    },
    onError: (error) => {
      console.error('Error updating invoice status:', error);
      notify.error(error);
    },
  });

  // Clone mutation
  const cloneInvoiceMutation = useMutation({
    mutationFn: async () => {
      if (!invoice) throw new Error('No invoice to clone');

      // 1. Insert new standalone draft invoice
      const { data: newInvoice, error: insertError } = await supabase
        .from('invoices')
        .insert({
          title: `Copy of ${invoice.title}`,
          description: invoice.description,
          customer_id: invoice.customer_id,
          company_id: invoice.company_id,
          created_by: invoice.created_by,
          estimate_id: null,
          invoice_type: 'standalone',
          status: 'draft',
          payment_terms: invoice.payment_terms,
          special_instructions: invoice.special_instructions,
          tax_inclusive: invoice.tax_inclusive,
          tax_percentage: invoice.tax_percentage,
          use_separate_site_address: invoice.use_separate_site_address,
          site_street_address: invoice.site_street_address,
          site_suburb: invoice.site_suburb,
          site_city: invoice.site_city,
          site_province_state: invoice.site_province_state,
          site_postal_code: invoice.site_postal_code,
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      // 2. Copy line items
      if (lineItems && lineItems.length > 0) {
        const newLineItems = lineItems.map((item, index) => ({
          invoice_id: newInvoice.id,
          description: item.description,
          base_unit: item.base_unit,
          unit_cost: item.unit_cost,
          quantity_invoiced: item.quantity_invoiced,
          total_cost: item.total_cost,
          sort_order: item.sort_order ?? index,
          base_cost: item.base_cost,
          markup_percentage: item.markup_percentage,
          category_id: item.category_id,
          source_type: item.source_type,
          source_item_id: item.source_item_id,
          // No estimate_line_item_id since it's standalone
        }));

        const { error: lineItemsError } = await supabase
          .from('invoice_line_items')
          .insert(newLineItems);

        if (lineItemsError) throw lineItemsError;
      }

      return newInvoice;
    },
    onSuccess: (data) => {
      notify.success('Invoice cloned successfully');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate(`/invoices/edit/${data.id}`);
    },
    onError: (error) => {
      console.error('Error cloning invoice:', error);
      notify.error(error);
    },
  });

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === invoice?.status) return;
    if (newStatus === 'paid' && remainingBalance > 0) {
      try {
        await quickPay();
      } catch (error) {
        // Error handled in hook
      }
      return;
    }
    updateStatusMutation.mutate(newStatus);
  };

  const handleApplyCredit = async (data: { creditId: string; invoiceId: string; amount: number }) => {
    try {
      await applyCredit({
        creditId: data.creditId,
        invoiceId: data.invoiceId,
        amount: data.amount,
      });
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      notify.success('Credit applied successfully');
    } catch (error) {
      notify.error(error);
    }
  };

  const getStatusConfig = () => {
    if (!invoice?.status) return undefined;
    
    const statusMap: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" | "success" }> = {
      draft: { label: 'Draft', variant: 'outline' },
      sent: { label: 'Sent', variant: 'secondary' },
      viewed: { label: 'Viewed', variant: 'secondary' },
      paid: { label: 'Paid', variant: 'success' },
      partial: { label: 'Partial', variant: 'default' },
      partially_paid: { label: 'Partially Paid', variant: 'default' },
      overdue: { label: 'Overdue', variant: 'destructive' },
      cancelled: { label: 'Cancelled', variant: 'destructive' },
    };
    
    return statusMap[invoice.status] || { label: invoice.status, variant: 'outline' };
  };

  if (isLoadingInvoice) {
    return (
      <DetailPageLayout>
        <div className="space-y-4">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-64 bg-muted animate-pulse rounded" />
        </div>
      </DetailPageLayout>
    );
  }

  if (!invoice) {
    return (
      <DetailPageLayout>
        <div className="text-center py-12">
          <p className="text-destructive font-semibold mb-4">Invoice not found</p>
          <Link to="/invoices" className="text-primary hover:underline">
            Back to Invoices
          </Link>
        </div>
      </DetailPageLayout>
    );
  }

  // Build secondary actions
  const secondaryActions: Array<{
    label: string;
    icon?: typeof Eye;
    onClick: () => void;
    destructive?: boolean;
    disabled?: boolean;
    requiresConfirmation?: { title: string; description: string };
    subActions?: Array<{ label: string; onClick: () => void }>;
  }> = [];

  // Preview & Send
  secondaryActions.push({
    label: "Preview / Download PDF",
    icon: FileText,
    onClick: () => setShowPreviewModal(true)
  });
  secondaryActions.push({
    label: "Send Invoice",
    icon: Send,
    onClick: () => setShowSendDialog(true)
  });

  // Payment actions - only if balance remaining
  if (remainingBalance > 0 && invoice.status !== 'cancelled') {
    secondaryActions.push({
      label: "Record Payment",
      icon: CreditCard,
      onClick: () => setShowPaymentModal(true)
    });
    secondaryActions.push({
      label: isQuickPaying ? "Processing..." : "Mark as Paid",
      icon: CheckCircle2,
      onClick: () => handleStatusChange('paid'),
    });
    secondaryActions.push({
      label: "Write Off Balance",
      icon: AlertTriangle,
      onClick: () => setShowWriteOffModal(true)
    });
  }

  if (canApplyCredit) {
    secondaryActions.push({
      label: "Apply Credit",
      icon: CreditCard,
      onClick: () => setShowApplyCreditModal(true)
    });
  }

  // Status changes - as a submenu
  const statusOptions = [
    { value: 'draft', label: 'Draft', variant: 'outline' as const },
    { value: 'sent', label: 'Sent', variant: 'secondary' as const },
    { value: 'partially_paid', label: 'Partially Paid', variant: 'secondary' as const },
    { value: 'paid', label: 'Paid', variant: 'default' as const },
    { value: 'overdue', label: 'Overdue', variant: 'destructive' as const },
    { value: 'cancelled', label: 'Cancelled', variant: 'destructive' as const },
  ].filter(s => s.value !== 'draft' || invoice.status === 'draft');
  secondaryActions.push({
    label: "Change Status",
    icon: RefreshCw,
    onClick: () => {},
    subActions: statusOptions.map(s => ({
      label: s.label,
      onClick: () => handleStatusChange(s.value),
      badge: { variant: s.variant },
      isCurrent: s.value === invoice.status,
      disabled: s.value === invoice.status,
    })),
  });

  // Clone
  secondaryActions.push({
    label: cloneInvoiceMutation.isPending ? "Cloning..." : "Clone Invoice",
    icon: Copy,
    onClick: () => cloneInvoiceMutation.mutate(),
    disabled: cloneInvoiceMutation.isPending,
  });

  // Delete
  secondaryActions.push({
    label: "Delete Invoice",
    icon: Trash2,
    onClick: () => deleteInvoiceMutation.mutate(),
    destructive: true,
    requiresConfirmation: {
      title: "Delete Invoice",
      description: "Are you sure you want to delete this invoice? This action cannot be undone."
    }
  });

  // Build site address for send dialog
  const siteAddress = [
    invoice.site_street_address,
    invoice.site_suburb,
    invoice.site_city,
    invoice.site_province_state,
    invoice.site_postal_code,
  ].filter(Boolean).join(', ');

  return (
    <DetailPageLayout>
      <DetailPageHeader
        title={invoice.invoice_number || 'Invoice'}
        subtitle={invoice.title}
        status={getStatusConfig()}
        backLink={{ label: "Back to Invoices", href: "/invoices" }}
        primaryAction={{
          label: "Edit",
          icon: Edit,
          onClick: () => navigate(`/invoices/edit/${id}`)
        }}
        secondaryActions={secondaryActions}
      />

      {/* Preview Modal - triggered from menu */}
      <InvoicePreviewModal
        invoiceId={id!}
        isOpen={showPreviewModal}
        onOpenChange={setShowPreviewModal}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <InvoiceDetailsCard invoice={invoice} linkedProject={linkedProject} />
          <InvoiceFinancialCard invoice={invoice} />
          <InvoiceAdditionalInfoCard invoice={invoice} />
          <DocumentSendHistory documentType="invoice" documentId={id!} customerId={invoice?.customer_id || invoice?.customers?.id} />
        </div>

        <InvoiceItemsTable 
          lineItems={lineItems || []} 
          isLoading={isLoadingLineItems} 
        />
      </div>

      {/* Apply Credit Modal */}
      <ApplyCreditsToInvoiceModal
        isOpen={showApplyCreditModal}
        onClose={() => setShowApplyCreditModal(false)}
        credit={availableCredit}
        customerId={invoice?.customer_id || invoice?.customers?.id || ''}
        onApply={handleApplyCredit}
        isLoading={isApplying}
      />

      {/* Payment Record Modal */}
      <PaymentRecordModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        invoice={{
          id: invoice.id,
          invoice_number: invoice.invoice_number || '',
          total_amount: invoice.total_amount || 0,
          paid_amount: invoice.paid_amount || 0,
          credit_applied: invoice.credit_applied || 0,
          customer_id: invoice.customer_id || invoice.customers?.id || '',
        }}
      />

      {/* Write Off Modal */}
      <WriteOffModal
        open={showWriteOffModal}
        onOpenChange={setShowWriteOffModal}
        invoice={{
          id: invoice.id,
          invoice_number: invoice.invoice_number || '',
          total_amount: invoice.total_amount || 0,
          paid_amount: invoice.paid_amount || 0,
        }}
      />

      {/* Send Invoice Dialog */}
      <SendInvoiceDialog
        open={showSendDialog}
        onOpenChange={setShowSendDialog}
        invoiceId={id!}
        invoiceNumber={invoice.invoice_number || undefined}
        customerId={invoice.customer_id || invoice.customers?.id}
        companyId={invoice.company_id}
        siteAddress={siteAddress || undefined}
      />
    </DetailPageLayout>
  );
};

export default InvoiceViewPage;
